import React, { CSSProperties, MouseEvent as ReactMouseEvent, memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { getOperation, useOperation } from "../../../../../../../../contexts/OperationContext";
import { getPlan } from '../../../../../../../../contexts/PlanOperationContext/Plan';
import { selectOpMode, selectPermissions } from '../../../../../../../../util/store/selectors';
import { KeyOfStep, OP_STATUS, OPERATION_TYPE, TIME_PER_STEP } from "../../../../../../../../util/varibles/constants";
import { cloneObj, durationTxtByStartAndFinish } from "../../../../../../../../util/varibles/global";
import { IActivityLog, IOperation } from "../../../../../../../../util/varibles/interface";
import { ACTION } from '../../../../../../../../util/varibles/permission';
import { planOpActions } from "../../../../../../reducer";
import { convertPxToTimeByStep } from "../../../../../../util";
import { getTimeByOpMode } from "../../../../../../util/function_operation/constants";
import { HEIGHT_PER_DAY } from "../../../constants";
import TimePoint from "./TimePoint";
import styles from './style.module.scss';

interface TimelineItem {
    key: number
    name: 'start' | 'site' | 'factory' | 'finish'
    label: string
    title?: string
}

type TimelineStyle = CSSProperties & Record<`--${string}`, number | string>;

const timeline: Partial<Record<OPERATION_TYPE, (data: IOperation, activityLog: IActivityLog[]) => TimelineItem[]>> = {
    [OPERATION_TYPE.HARVEST]: (data: IOperation, activityLog: IActivityLog[]) => {
        const { site_name, sites = [] } = data.operation;
        const listOfSite = sites.length === 0 ? [{ name: site_name }] : sites;
        let indexSite = 0;
        const listTimeOfSite: TimelineItem[] = [];
        activityLog.forEach((item, index) => {
            if (item.key === KeyOfStep.ARRIVE_SITE && index) {
                const { name = '' } = listOfSite[indexSite] || {};
                indexSite++;
                listTimeOfSite.push({ key: index, name: 'site', label: 'Site', title: name });
            }
        });
        const indexFactory = activityLog.findIndex(item => item.key === KeyOfStep.ARRIVE_FACTORY);
        return [
            { key: 0, name: 'start', label: 'Start' },
            ...listTimeOfSite,
            ...(indexFactory >= 0 ? [{ key: indexFactory, name: 'factory' as const, label: 'Factory' }] : []),
            { key: -1, name: 'finish', label: 'Complete' }
        ]
    },
    [OPERATION_TYPE.TREATMENT]: () => [
        { key: 0, name: 'start', label: 'Start' },
        { key: -1, name: 'finish', label: 'Complete' }
    ],
    [OPERATION_TYPE.SERVICE]: () => [
        { key: 0, name: 'start', label: 'Start' },
        { key: -1, name: 'finish', label: 'Complete' }
    ],
    [OPERATION_TYPE.TRANSPORT]: () => [
        { key: 0, name: 'start', label: 'Start' },
        { key: 1, name: 'site', label: 'Smolt site' },
        { key: 5, name: 'site', label: 'Site' },
        { key: -1, name: 'finish', label: 'Complete' }
    ],
    [OPERATION_TYPE.EVENT]: () => [
        { key: 0, name: 'start', label: 'Start' },
        { key: -1, name: 'finish', label: 'Complete' }
    ],
    [OPERATION_TYPE.SUPPORT]: () => [
        { key: 0, name: 'start', label: 'Start' },
        { key: -1, name: 'finish', label: 'Complete' }
    ],
}
const Duration = memo(({ start, finish, style, isOverlap }: { start: number, finish: number, style: TimelineStyle, isOverlap?: boolean }) => {
    const durationStyle: TimelineStyle = { ...style, '--start-duration-timeline': finish };
    return <div className={styles.duration} style={durationStyle as CSSProperties} data-overlap={isOverlap}>
        <span>{durationTxtByStartAndFinish(start, finish)}</span>
    </div>;
});

const TIME_LABEL_HEIGHT = 18;
const TIMELINE_START_WIDTH = 110;
const MS_PER_DAY = 86400000;
const ignoreMouseDown = () => undefined;

interface IProps {
    isOwn: boolean
    activityLog: IActivityLog[]
    data: IOperation
    width: number
    adjust: { leftTimeLine?: string }
}

const TimeLine: React.FC<IProps> = (props) => {
    const { data } = props;
    const operation_mode = useSelector(selectOpMode);
    const [isTimeline, setOperation] = useOperation(state => state.isTimeline);
    const [canEditTimeline] = useOperation(state => state.canEditTimeline);
    const [left] = useOperation(state => state.left);
    const [index] = useOperation(state => state.vesselIndex);
    const [canUpdateOperation] = useSelector(selectPermissions([ACTION.PLAN_OP.UPDATE_OP]));
    const dispatch = useDispatch();
    const targetYRef = useRef(0);
    const dragStateRef = useRef(-1);
    const dragHandlersRef = useRef<{
        mouseMove(e: MouseEvent): void;
        mouseUp(e: MouseEvent): void;
    }>();
    const onDocumentMouseMove = useCallback((e: MouseEvent) => {
        dragHandlersRef.current?.mouseMove(e);
    }, []);
    const onDocumentMouseUp = useCallback((e: MouseEvent) => {
        dragHandlersRef.current?.mouseUp(e);
    }, []);
    const funcRef = useRef({
        getPlan: getPlan(),
        get: getOperation()
    })


    const handleMouseDown = (e: ReactMouseEvent, index: number) => {
        e.stopPropagation();
        e.preventDefault();
        if (e.button !== 0)
            return;

        const { current_process } = data.operation;
        if (index && index <= current_process)
            return;
        const { pageY } = e;
        targetYRef.current = pageY;
        dragStateRef.current = index;
        document.addEventListener('mousemove', onDocumentMouseMove);
        document.addEventListener('mouseup', onDocumentMouseUp);
        setOperation({ modeDrag: 'time' });
        dispatch(planOpActions.setFocusId(data.operation.id));
    }

    const updateTime = (e: MouseEvent | ReactMouseEvent): IActivityLog[] => {
        const { pageY } = e;
        const { activity_log } = data;
        const { current_process } = data.operation;
        const targetY = targetYRef.current;
        const dragState = dragStateRef.current;
        const space = pageY - targetY;
        if (!space)
            return activity_log;

        const durationMax = (activity_log[dragState].est_start_time - activity_log[0].est_start_time) / 2;
        const duration = convertPxToTimeByStep(pageY - targetY);
        const max = activity_log.length - 1;

        if (dragState === 0) {
            return activity_log.map((item: any, i) => ({
                ...item,
                est_start_time: (duration < 0 && max === i) ? item.est_start_time : item.est_start_time + duration
            }));
        }

        let rest = Math.max(duration, -durationMax);
        const adjustment: Record<number, number> = {};
        for (let i = activity_log.length - 1; i > 0; i--) {
            if (i <= current_process) {
                break;
            } else if (i > dragState) {
                adjustment[i] = rest;
            } else {
                const time = activity_log[i].est_start_time - activity_log[i - 1].est_start_time;
                const limit = -time / 2;
                adjustment[i] = Math.max(time + limit, time + rest);
                rest -= limit
                if (rest >= 0)
                    break;
            }
        }

        return activity_log.reduce((rs: IActivityLog[], item, i) => {
            if (!adjustment[i])
                return rs;
            const { est_start_time: prev } = rs[i - 1];
            if (i > dragState) {
                const { est_start_time: current } = rs[i];
                rs[i] = { ...item, est_start_time: Math.max(prev + TIME_PER_STEP, current + adjustment[i]) };
            } else {
                rs[i] = { ...item, est_start_time: prev + adjustment[i] };
            }
            return rs;
        }, cloneObj(activity_log));
    }

    const handleMouseMove = (e: MouseEvent) => {
        setOperation(prev => ({ activityLogs: { ...prev.activityLogs, [prev.rootKey]: updateTime(e) } }));
    };

    const handleMouseUp = (e: MouseEvent) => {
        document.removeEventListener('mousemove', onDocumentMouseMove);
        document.removeEventListener('mouseup', onDocumentMouseUp);

        setOperation({ modeDrag: '' });
        const { updateOp } = funcRef.current.get();
        updateOp?.({ activity_log: updateTime(e) })
    }

    dragHandlersRef.current = {
        mouseMove: handleMouseMove,
        mouseUp: handleMouseUp
    };

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', onDocumentMouseMove);
            document.removeEventListener('mouseup', onDocumentMouseUp);
        };
    }, [onDocumentMouseMove, onDocumentMouseUp]);

    if (!isTimeline)
        return null;

    const { activityLog, width, isOwn, adjust } = props;
    const { operation_type, id, status, current_process = -1 } = props.data.operation;
    const lastIndex = activityLog.length - 1;
    const { calendarRef } = funcRef.current.getPlan();
    const el = calendarRef?.();

    if (!el || lastIndex < 0)
        return null;

    const { scrollLeft } = el;
    const style: TimelineStyle = { left: scrollLeft + 'px' };

    if (left !== undefined) {
        const widthFromViewportStart = left - scrollLeft + TIMELINE_START_WIDTH;
        style.width = `${Math.max(TIMELINE_START_WIDTH, widthFromViewportStart)}px`;
    } else {
        style.transition = 'width .2s';
        style.width = adjust.leftTimeLine ? adjust.leftTimeLine : `calc(${width * index}% + ${120 - scrollLeft}px)`;
    }

    const generateData = timeline[operation_type];

    const list = generateData ? generateData(props.data, activityLog) : [];
    const isEdit = isOwn && status !== OP_STATUS.FINISHED && canUpdateOperation && canEditTimeline;
    const startTime = getTimeByOpMode[operation_mode](activityLog[0]);
    const finishTime = getTimeByOpMode[operation_mode](activityLog[lastIndex]);
    const isStartCompleteOverlap = (finishTime - startTime) / MS_PER_DAY * HEIGHT_PER_DAY <= TIME_LABEL_HEIGHT;

    return <>
        {list.map(item => {
            const { key } = item
            const index = key !== -1 ? key : lastIndex;
            const time = getTimeByOpMode[operation_mode](activityLog[index]);
            const { real_start_time } = activityLog[index];
            const isAllow = isEdit && (index > current_process || index === 0) && !real_start_time;
            return <TimePoint key={key} {...{
                id,
                data: item,
                isEdit: isAllow,
                style,
                index,
                time,
                isOverlap: isStartCompleteOverlap && (item.name === 'start' || item.name === 'finish'),
                handleMouseDown: isAllow ? handleMouseDown : ignoreMouseDown
            }} />
        })}
        <Duration
            style={style}
            start={startTime}
            finish={finishTime}
            isOverlap={isStartCompleteOverlap}
        />
    </>
};

export default TimeLine;
